import { Drawer } from "@material-tailwind/react";
import { Button } from "@material-tailwind/react";
import Skeleton from "react-loading-skeleton";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { IoCloseCircle } from "react-icons/io5";
import { IoIosArrowDown } from "react-icons/io";
import { HiDotsVertical } from "react-icons/hi";
import { BsArrowUpCircle } from "react-icons/bs";
import { RiEditFill } from "react-icons/ri";
import { MdDeleteSweep } from "react-icons/md";
import { BiSolidMessage, BiSolidReport } from "react-icons/bi";
import { FaHeart, FaRegHeart, FaUserSlash } from "react-icons/fa";
import toast from "react-hot-toast";
import { useCallback, useEffect, useRef, useState } from "react";
import { accountBlockUser } from "../../redux/ApiSlice/AccountProfileSlice";
import { reportNewsComment } from "../../redux/ApiSlice/Neighborhood/NeighborhoodNewsReportSlice";
import { handleProfileNavigate } from "../../utils/handleProfileNavigate";
import {
    createNeighborhoodNewsComment,
    deleteNeighborhoodNewsComment,
    getNeighborhoodNewsComment,
    getNeighborhoodNewsCommentReplies,
    likeDislikeNeighborhoodNewsComment,
    likeDislikeNeighborhoodNewsCommentReply,
    updateNeighborhoodNewsComment,
    createNeighborhoodNewsCommentReply,
    updateNeighborhoodNewsCommentReply,
    deleteNeighborhoodNewsCommentReply,
} from "../../redux/ApiSlice/Neighborhood/NeighBorhoodNewsCommentSlice";

const REPORT_REASONS = [
    "Harassment", "Inappropriate", "Bullying or targeted harrassment", "Hate speech",
    "Blackmail", "Sexual Harassment", "Impersonation/Fake account", "Graphic Violence/Gore",
    "Spam", "Drugs/Weapons", "Sexual content involving suspected minors", "Stalking or Intimidation",
    "Child safety concerns", "Self-harm or suicide content", "Violence or physical harm threats",
    "Dangerous acts & challenges", "Human exploitation/trafficking", "Scams/Fraud/Phishing",
    "Illegal Services/Solicitation", "IP Infringement", "Other",
];

function timeAgo(timestamp) {
    const now = new Date();
    const past = new Date(timestamp);
    const seconds = Math.floor((now - past) / 1000);
    const intervals = { year: 31536000, month: 2592000, day: 86400, hour: 3600, minute: 60, second: 1 };
    for (const key in intervals) {
        const interval = Math.floor(seconds / intervals[key]);
        if (interval >= 1) return `${interval}${key[0]} ago`;
    }
    return "1s ago";
}

function SkeletonList() {
    return (
        <>
            {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="grid grid-cols-12 p-2 py-1">
                    <div className="col-span-2 flex justify-start items-center">
                        <Skeleton height={30} width={30} circle />
                    </div>
                    <div className="col-span-10 -ml-3">
                        <Skeleton height={15} width={150} />
                        <Skeleton height={15} width={100} />
                    </div>
                </div>
            ))}
        </>
    );
}

export default function NeighborhoodNewsComments({ newsId, isOpen, onClose, onCommentCountChange }) {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { User_profile } = useSelector((state) => state.AccountProfile);
    const commentsState = useSelector((state) => state.NeighborhoodNewsComment);

    const handleNavigate = handleProfileNavigate(navigate, User_profile?.id);

    const [commentsData, setCommentsData] = useState([]);
    const [hasMore, setHasMore] = useState(true);
    const [loadingPage, setLoadingPage] = useState(false);
    const pageRef = useRef(1);

    const [commentBody, setCommentBody] = useState("");
    const [editingComment, setEditingComment] = useState(null);
    const [editCommentBody, setEditCommentBody] = useState("");

    const [replyingTo, setReplyingTo] = useState(null);
    const [replyBody, setReplyBody] = useState("");
    const [editingReply, setEditingReply] = useState(null);

    const [showReplies, setShowReplies] = useState(null);
    const [repliesData, setRepliesData] = useState({});
    const [replyLoading, setReplyLoading] = useState(false);

    const [menuCommentId, setMenuCommentId] = useState(null);
    const [menuReplyId, setMenuReplyId] = useState(null);

    const [reportingCommentId, setReportingCommentId] = useState(null);
    const [reportReason, setReportReason] = useState("");
    const [reportMessage, setReportMessage] = useState("");
    const [showReportReasonDropdown, setShowReportReasonDropdown] = useState(false);

    const [blockUser, setBlockUser] = useState(null);
    const [showBlockConfirm, setShowBlockConfirm] = useState(false);

    const scrollRef = useRef(null);
    const inputRef = useRef(null);
    const menuRef = useRef(null);
    const isFetchingRef = useRef(false);

    const fetchComments = useCallback(async (pageNum) => {
        if (isFetchingRef.current) return;
        isFetchingRef.current = true;
        setLoadingPage(true);
        try {
            const response = await dispatch(getNeighborhoodNewsComment({ id: newsId, page: pageNum })).unwrap();
            if (response?.results) {
                setCommentsData((prev) => pageNum === 1 ? response.results : [...prev, ...response.results]);
                setHasMore(!!response.next);
                pageRef.current = pageNum;
            }
        } catch {
            // silent
        } finally {
            isFetchingRef.current = false;
            setLoadingPage(false);
        }
    }, [dispatch, newsId]);

    useEffect(() => {
        if (isOpen && newsId) {
            pageRef.current = 1;
            isFetchingRef.current = false;
            fetchComments(1);
        }
        if (!isOpen) {
            setCommentsData([]);
            setHasMore(true);
            setEditingComment(null);
            setReplyingTo(null);
            setEditingReply(null);
            setMenuCommentId(null);
            setMenuReplyId(null);
            setReportingCommentId(null);
            setShowReplies(null);
            setRepliesData({});
            setCommentBody("");
            setReplyBody("");
        }
    }, [isOpen, newsId, fetchComments]);

    const handleScroll = () => {
        const div = scrollRef.current;
        if (!div || loadingPage || !hasMore) return;
        const { scrollTop, scrollHeight, clientHeight } = div;
        if (scrollTop + clientHeight >= scrollHeight - 5) {
            fetchComments(pageRef.current + 1);
        }
    };

    // Comment submit (create or update)
    const handleSubmitComment = async () => {
        if (editingComment) {
            if (!editCommentBody.trim()) return;
            try {
                await dispatch(updateNeighborhoodNewsComment({ commentId: editingComment.id, data: { body: editCommentBody } })).unwrap();
                setCommentsData((prev) =>
                    prev.map((c) => c.id === editingComment.id ? { ...c, body: editCommentBody } : c)
                );
                setEditingComment(null);
                setEditCommentBody("");
            } catch {
                toast.error("Failed to update comment.");
            }
        } else {
            if (!commentBody.trim()) return;
            try {
                const response = await dispatch(createNeighborhoodNewsComment({ id: newsId, data: { body: commentBody } })).unwrap();
                if (response) {
                    setCommentsData((prev) => [response, ...prev]);
                    setCommentBody("");
                    if (onCommentCountChange) onCommentCountChange(newsId, 1);
                }
            } catch {
                toast.error("Failed to post comment.");
            }
        }
    };

    const handleDeleteComment = async (commentId) => {
        try {
            await dispatch(deleteNeighborhoodNewsComment({ commentId })).unwrap();
            setCommentsData((prev) => prev.filter((c) => c.id !== commentId));
            setMenuCommentId(null);
            if (onCommentCountChange) onCommentCountChange(newsId, -1);
        } catch {
            toast.error("Failed to delete comment.");
        }
    };

    const handleLikeComment = async (commentId) => {
        setCommentsData((prev) =>
            prev.map((c) => c.id === commentId
                ? { ...c, is_liked: !c.is_liked, likes_count: c.is_liked ? c.likes_count - 1 : c.likes_count + 1 }
                : c
            )
        );
        try {
            await dispatch(likeDislikeNeighborhoodNewsComment({ commentId }));
        } catch {
            // revert on error
            setCommentsData((prev) =>
                prev.map((c) => c.id === commentId
                    ? { ...c, is_liked: !c.is_liked, likes_count: c.is_liked ? c.likes_count - 1 : c.likes_count + 1 }
                    : c
                )
            );
        }
    };

    // Reply handlers
    const handleSubmitReply = async () => {
        if (!replyBody.trim()) return;
        if (editingReply) {
            try {
                await dispatch(updateNeighborhoodNewsCommentReply({ replyId: editingReply.id, data: { body: replyBody } })).unwrap();
                const parentId = replyingTo?.id;
                setRepliesData((prev) => ({
                    ...prev,
                    [parentId]: (prev[parentId] || []).map((r) => r.id === editingReply.id ? { ...r, body: replyBody } : r),
                }));
                setEditingReply(null);
                setReplyingTo(null);
                setReplyBody("");
            } catch {
                toast.error("Failed to update reply.");
            }
        } else {
            try {
                const response = await dispatch(createNeighborhoodNewsCommentReply({ commentId: replyingTo.id, data: { body: replyBody } })).unwrap();
                if (response) {
                    setCommentsData((prev) =>
                        prev.map((c) => c.id === replyingTo.id ? { ...c, reply_count: c.reply_count + 1 } : c)
                    );
                    setRepliesData((prev) => ({
                        ...prev,
                        [replyingTo.id]: [...(prev[replyingTo.id] || []), response],
                    }));
                    if (showReplies !== replyingTo.id) setShowReplies(replyingTo.id);
                    setReplyBody("");
                    setReplyingTo(null);
                }
            } catch {
                toast.error("Failed to post reply.");
            }
        }
    };

    const handleDeleteReply = async (replyId, commentId) => {
        try {
            await dispatch(deleteNeighborhoodNewsCommentReply({ replyId })).unwrap();
            setRepliesData((prev) => ({
                ...prev,
                [commentId]: (prev[commentId] || []).filter((r) => r.id !== replyId),
            }));
            setCommentsData((prev) =>
                prev.map((c) => c.id === commentId ? { ...c, reply_count: Math.max(0, c.reply_count - 1) } : c)
            );
            setMenuReplyId(null);
        } catch {
            toast.error("Failed to delete reply.");
        }
    };

    const handleLikeReply = async (replyId, commentId) => {
        setRepliesData((prev) => ({
            ...prev,
            [commentId]: (prev[commentId] || []).map((r) =>
                r.id === replyId
                    ? { ...r, is_liked: !r.is_liked, like_count: r.is_liked ? r.like_count - 1 : r.like_count + 1 }
                    : r
            ),
        }));
        try {
            await dispatch(likeDislikeNeighborhoodNewsCommentReply({ replyId }));
        } catch {
            // revert
            setRepliesData((prev) => ({
                ...prev,
                [commentId]: (prev[commentId] || []).map((r) =>
                    r.id === replyId
                        ? { ...r, is_liked: !r.is_liked, like_count: r.is_liked ? r.like_count - 1 : r.like_count + 1 }
                        : r
                ),
            }));
        }
    };

    const handleShowReplies = async (commentId) => {
        if (showReplies === commentId) {
            setShowReplies(null);
            return;
        }
        setShowReplies(commentId);
        if (!repliesData[commentId]) {
            setReplyLoading(true);
            try {
                const response = await dispatch(getNeighborhoodNewsCommentReplies(commentId)).unwrap();
                setRepliesData((prev) => ({ ...prev, [commentId]: response }));
            } catch {
                // silent
            } finally {
                setReplyLoading(false);
            }
        }
    };

    const handleSubmitReport = async () => {
        if (!reportReason) return toast.error("Please select a reason.");
        try {
            await dispatch(reportNewsComment({ commentId: reportingCommentId, data: { reason: reportReason, message: reportMessage } })).unwrap();
            toast.success("Comment reported successfully.");
            setReportingCommentId(null);
            setReportReason("");
            setReportMessage("");
        } catch {
            toast.error("Failed to report comment.");
        }
    };

    const handleBlockConfirm = async () => {
        try {
            await dispatch(accountBlockUser(blockUser.id));
            toast.success(`${blockUser.full_name} blocked successfully.`);
        } catch {
            toast.error("Failed to block user.");
        }
        setShowBlockConfirm(false);
        setBlockUser(null);
    };

    const handleCommentKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSubmitComment(); }
    };

    const handleReplyKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSubmitReply(); }
    };

    useEffect(() => {
        if ((replyingTo || editingComment) && inputRef.current) {
            setTimeout(() => inputRef.current?.focus(), 100);
        }
    }, [replyingTo, editingComment]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setMenuCommentId(null);
                setMenuReplyId(null);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    return (
        <>
            <Drawer
                placement="bottom"
                open={isOpen}
                onClose={onClose}
                size={window.innerWidth < 500 ? 320 : 300}
                className={`absolute w-full rounded-t-xl ${!isOpen && "hidden"}`}
            >
                <div className="h-full w-full bg-white dark:bg-[#1E1D21] rounded-t-xl pb-10 overflow-hidden">
                    <div
                        ref={scrollRef}
                        onScroll={handleScroll}
                        onTouchStartCapture={(e) => e.stopPropagation()}
                        onTouchMoveCapture={(e) => e.stopPropagation()}
                        onWheelCapture={(e) => e.stopPropagation()}
                        className="bg-white dark:bg-[#1E1D21] dark:text-white rounded-t-xl w-full h-full overflow-y-scroll no-scrollbar touch-pan-y overscroll-contain"
                    >
                        {(commentsState.loading && commentsData.length === 0) && <SkeletonList />}

                        {commentsData.length > 0 && (
                            <div className="w-full">
                                {commentsData.map((comment) => (
                                    <div key={comment.id} className="py-2 px-2 border-b border-gray-300 dark:border-gray-800/50 relative">
                                        <div className="flex items-start">
                                            <div className="h-7 w-10">
                                                <img
                                                    alt="profile"
                                                    src={comment.created_by?.avatar}
                                                    onClick={() => handleNavigate(comment.created_by?.id)}
                                                    className="h-7 w-7 rounded-full object-cover bg-gray-300 dark:bg-gray-800 cursor-pointer"
                                                />
                                            </div>
                                            <div className="flex flex-col w-full">
                                                <div className="flex items-start justify-between">
                                                    <div className="flex items-start gap-x-2 relative">
                                                        <div className="flex flex-col">
                                                            <p
                                                                onClick={() => handleNavigate(comment.created_by?.id)}
                                                                className="text-[14px] font-bold line-clamp-1 cursor-pointer dark:text-white"
                                                            >
                                                                {comment.created_by?.full_name}
                                                            </p>
                                                            <p className="text-[8px] text-gray-500">{timeAgo(comment.created_at)}</p>
                                                        </div>
                                                        <HiDotsVertical
                                                            onClick={(e) => { e.stopPropagation(); setMenuCommentId(menuCommentId === comment.id ? null : comment.id); }}
                                                            className="text-[12px] cursor-pointer text-gray-600 mt-[3px]"
                                                        />
                                                        {menuCommentId === comment.id && (
                                                            <div
                                                                ref={menuRef}
                                                                onClick={(e) => e.stopPropagation()}
                                                                className="absolute top-0 -right-14 w-auto bg-white dark:bg-[#1E1D21] border-2 border-indigo-100 overflow-hidden rounded shadow-lg z-20"
                                                            >
                                                                <p
                                                                    onClick={() => { setReplyingTo(comment); setEditingReply(null); setReplyBody(""); setMenuCommentId(null); }}
                                                                    className="px-1.5 py-[1px] text-[8px] text-gray-700 dark:text-white dark:hover:bg-[#444444] cursor-pointer flex items-center gap-x-1 whitespace-nowrap border-b border-gray-200 dark:border-gray-700"
                                                                >
                                                                    <BiSolidMessage className="mt-[2px]" /> Reply
                                                                </p>
                                                                {User_profile?.id !== comment.created_by?.id && (
                                                                    <p
                                                                        onClick={() => { setReportingCommentId(comment.id); setMenuCommentId(null); }}
                                                                        className="px-1.5 py-[1px] text-[8px] text-gray-700 dark:text-white dark:hover:bg-[#444444] cursor-pointer flex items-center gap-x-1 whitespace-nowrap border-b border-gray-200 dark:border-gray-700"
                                                                    >
                                                                        <BiSolidReport className="mt-[-0.5px]" /> Report
                                                                    </p>
                                                                )}
                                                                {User_profile?.id !== comment.created_by?.id && (
                                                                    <p
                                                                        onClick={() => { setBlockUser(comment.created_by); setShowBlockConfirm(true); setMenuCommentId(null); }}
                                                                        className="px-1.5 py-[1px] text-[8px] text-gray-700 dark:text-white dark:hover:bg-[#444444] cursor-pointer flex items-center gap-x-1 whitespace-nowrap"
                                                                    >
                                                                        <FaUserSlash className="mt-[-0.5px]" /> Block
                                                                    </p>
                                                                )}
                                                                {User_profile?.id === comment.created_by?.id && (
                                                                    <>
                                                                        <p
                                                                            onClick={() => { setEditingComment(comment); setEditCommentBody(comment.body); setMenuCommentId(null); }}
                                                                            className="px-1.5 py-[1px] text-[8px] text-gray-700 dark:text-white dark:hover:bg-[#444444] cursor-pointer flex items-center gap-x-1 whitespace-nowrap border-b border-gray-200 dark:border-gray-700"
                                                                        >
                                                                            <RiEditFill /> Edit
                                                                        </p>
                                                                        <p
                                                                            onClick={() => handleDeleteComment(comment.id)}
                                                                            className="px-1.5 py-[1px] text-[8px] text-gray-700 dark:text-white dark:hover:bg-[#444444] cursor-pointer flex items-center gap-x-1 whitespace-nowrap"
                                                                        >
                                                                            <MdDeleteSweep className="mt-[-1px]" /> Delete
                                                                        </p>
                                                                    </>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>

                                                    <div
                                                        onClick={() => handleLikeComment(comment.id)}
                                                        className="flex items-center gap-x-1 cursor-pointer"
                                                    >
                                                        <p className="text-[10px] font-semibold text-gray-500">{comment.likes_count}</p>
                                                        {comment.is_liked
                                                            ? <FaHeart className="text-[10px] text-red-800" />
                                                            : <FaRegHeart className="text-[10px] text-gray-600" />
                                                        }
                                                    </div>
                                                </div>

                                                <p className="text-[10px] w-[80%] mt-0.5 break-words leading-[14px] dark:text-gray-200">
                                                    {comment.body}
                                                </p>

                                                <p
                                                    onClick={() => { setReplyingTo(comment); setEditingReply(null); setReplyBody(""); }}
                                                    className="text-[10px] font-semibold text-gray-700 dark:text-gray-400 cursor-pointer mt-0.5 w-fit"
                                                >
                                                    Reply
                                                </p>

                                                {comment.reply_count > 0 && (
                                                    <div
                                                        onClick={() => handleShowReplies(comment.id)}
                                                        className="flex items-center gap-x-1 mt-1"
                                                    >
                                                        <IoIosArrowDown
                                                            className={`text-[14px] text-gray-700 cursor-pointer transform transition-transform duration-200 ${showReplies === comment.id ? "rotate-180" : "rotate-0"}`}
                                                        />
                                                        <p className="text-[10px] font-semibold text-gray-700 cursor-pointer">
                                                            {showReplies === comment.id ? "Close replies" : `View ${comment.reply_count} ${comment.reply_count === 1 ? "reply" : "replies"}`}
                                                        </p>
                                                    </div>
                                                )}

                                                {comment.reply_count > 0 && showReplies === comment.id && (
                                                    <div className="h-[0.5px] bg-gray-300 dark:bg-gray-800 w-[86%] mt-1.5" />
                                                )}

                                                {replyLoading && showReplies === comment.id && (
                                                    [1, 2, 3].map((i) => (
                                                        <div key={i} className="grid grid-cols-12 py-1">
                                                            <div className="col-span-2 flex justify-start items-center">
                                                                <Skeleton height={28} width={28} circle />
                                                            </div>
                                                            <div className="col-span-10 -ml-3">
                                                                <Skeleton height={12} width={120} />
                                                                <Skeleton height={12} width={80} />
                                                            </div>
                                                        </div>
                                                    ))
                                                )}

                                                {showReplies === comment.id && (repliesData[comment.id] || []).map((reply) => (
                                                    <div key={reply.id} className="flex w-full mt-1.5">
                                                        <div className="h-7 w-10">
                                                            <img
                                                                alt="User"
                                                                src={reply.created_by?.avatar_url}
                                                                onClick={() => handleNavigate(reply.created_by?.id)}
                                                                className="h-7 w-7 object-cover rounded-full cursor-pointer bg-gray-300 dark:bg-gray-800"
                                                            />
                                                        </div>
                                                        <div className="flex items-start justify-between w-full">
                                                            <div className="flex flex-col w-fit">
                                                                <div className="flex items-start gap-x-2 relative">
                                                                    <p className="text-[11px] font-bold line-clamp-1 dark:text-white">
                                                                        {reply.created_by?.full_name}
                                                                    </p>
                                                                    <HiDotsVertical
                                                                        onClick={() => setMenuReplyId(menuReplyId === reply.id ? null : reply.id)}
                                                                        className="text-[12px] cursor-pointer text-gray-600 mt-0.5"
                                                                    />
                                                                    {menuReplyId === reply.id && (
                                                                        <div
                                                                            ref={menuRef}
                                                                            className="absolute top-0 -right-12 w-auto bg-white dark:bg-[#1E1D21] border border-gray-400 overflow-hidden rounded shadow-lg z-20"
                                                                        >
                                                                            {reply.created_by?.id === User_profile?.id ? (
                                                                                <>
                                                                                    <p
                                                                                        onClick={() => { setEditingReply(reply); setReplyingTo(comment); setReplyBody(reply.body); setMenuReplyId(null); }}
                                                                                        className="px-1.5 py-[1px] text-[8px] text-gray-700 dark:text-white hover:bg-gray-300 dark:hover:bg-gray-700 cursor-pointer flex items-center gap-x-1 whitespace-nowrap"
                                                                                    >
                                                                                        <RiEditFill /> Edit
                                                                                    </p>
                                                                                    <p
                                                                                        onClick={() => handleDeleteReply(reply.id, comment.id)}
                                                                                        className="px-1.5 py-[1px] text-[8px] text-gray-700 dark:text-white hover:bg-gray-300 dark:hover:bg-gray-700 cursor-pointer flex items-center gap-x-1 whitespace-nowrap"
                                                                                    >
                                                                                        <MdDeleteSweep /> Delete
                                                                                    </p>
                                                                                </>
                                                                            ) : (
                                                                                <>
                                                                                    <p
                                                                                        onClick={() => { setBlockUser(reply.created_by); setShowBlockConfirm(true); setMenuReplyId(null); }}
                                                                                        className="px-1.5 py-[1px] text-[8px] text-gray-700 dark:text-white hover:bg-gray-300 dark:hover:bg-gray-700 cursor-pointer flex items-center gap-x-1 whitespace-nowrap"
                                                                                    >
                                                                                        <FaUserSlash /> Block
                                                                                    </p>
                                                                                </>
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                                <p className="text-[8px] text-gray-500">{timeAgo(reply.created_at)}</p>
                                                                <p className="text-[10px] mt-[3px] break-words leading-[14px] dark:text-gray-200">{reply.body}</p>
                                                            </div>
                                                            <div
                                                                className="flex flex-col items-center w-5 mt-[3px] cursor-pointer"
                                                                onClick={() => handleLikeReply(reply.id, comment.id)}
                                                            >
                                                                {reply.is_liked
                                                                    ? <FaHeart className="text-[10px] text-red-800" />
                                                                    : <FaRegHeart className="text-[10px] text-gray-600" />
                                                                }
                                                                <p className="text-[8px] text-gray-500">{reply.like_count}</p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                {loadingPage && commentsData.length > 0 && (
                                    <div className="py-3 flex justify-center">
                                        <SkeletonList />
                                    </div>
                                )}
                            </div>
                        )}

                        {!commentsState.loading && !loadingPage && commentsData.length === 0 && (
                            <div className="flex flex-col justify-center items-center mt-20">
                                <p className="text-[12px] text-gray-600 dark:text-gray-500">No comments. Be the first to comment!</p>
                            </div>
                        )}
                    </div>

                    {/* Input area — comment / edit comment / reply */}
                    {!replyingTo && !editingComment ? (
                        <div className="px-2 absolute bottom-1 pt-1 z-50 flex items-center w-full bg-white dark:bg-[#1E1D21] border-t border-gray-300 dark:border-gray-800/50">
                            <div className="h-8 w-11">
                                <img alt="profile" src={User_profile?.avatar} className="h-8 w-8 rounded-full object-cover bg-gray-300 dark:bg-gray-800" />
                            </div>
                            <div className="flex justify-start w-full relative">
                                <input
                                    type="text"
                                    ref={inputRef}
                                    value={commentBody}
                                    onChange={(e) => setCommentBody(e.target.value)}
                                    onKeyDown={handleCommentKeyDown}
                                    placeholder="Type your comment"
                                    className="text-[14px] w-full py-2 bg-gray-200 dark:bg-[#333333] text-black dark:text-white pl-2 pr-10 focus:outline-none rounded-md border-none"
                                />
                                <button
                                    onClick={handleSubmitComment}
                                    className="absolute top-1 right-1 bg-[#BDE6FF] flex items-center justify-center h-[30px] w-[30px] rounded-full cursor-pointer z-50"
                                >
                                    <BsArrowUpCircle className="text-[20px] text-black" />
                                </button>
                            </div>
                        </div>
                    ) : editingComment ? (
                        <div className="px-2 absolute bottom-[4px] z-50 flex flex-col bg-white dark:bg-black w-full pt-1 border-t border-gray-300 dark:border-gray-800">
                            <p className="text-[10px] text-blue-700 font-bold">Edit your Comment</p>
                            <div className="py-2 w-full">
                                <div className="flex items-start">
                                    <div className="h-7 w-10">
                                        <img alt="profile" src={editingComment.created_by?.avatar} className="h-7 w-7 rounded-full object-cover bg-gray-300" />
                                    </div>
                                    <div className="flex flex-col w-full">
                                        <div className="flex justify-between items-center w-full">
                                            <p className="text-[11px] font-bold text-black dark:text-white">{editingComment.created_by?.full_name}</p>
                                            <IoCloseCircle onClick={() => { setEditingComment(null); setEditCommentBody(""); }} className="text-gray-500 cursor-pointer text-[18px] hover:text-gray-700" />
                                        </div>
                                        <p className="text-[10px] mt-[3px] break-words text-black dark:text-white">{editingComment.body}</p>
                                    </div>
                                </div>
                            </div>
                            <div className="w-full z-50 flex">
                                <div className="h-8 w-11">
                                    <img alt="profile" src={User_profile?.avatar} className="h-8 w-8 rounded-full object-cover bg-gray-300 dark:bg-gray-800" />
                                </div>
                                <div className="flex justify-start w-full relative">
                                    <input
                                        type="text"
                                        ref={inputRef}
                                        value={editCommentBody}
                                        onChange={(e) => setEditCommentBody(e.target.value)}
                                        onKeyDown={handleCommentKeyDown}
                                        placeholder="Edit comment"
                                        className="text-[14px] bg-gray-200 w-full text-black dark:text-white pl-2 pr-7 py-1 outline-none rounded"
                                    />
                                    <BsArrowUpCircle onClick={handleSubmitComment} className="absolute right-1 w-5 top-[3px] text-blue-600 hover:text-blue-700 text-[22px] cursor-pointer" />
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="px-2 absolute bottom-[4px] z-50 flex flex-col bg-white dark:bg-[#1E1D21] w-full pt-1.5 border-t border-gray-300 dark:border-gray-800/50">
                            <div className="pb-2 w-full">
                                <div className="flex items-start">
                                    <div className="h-7 w-10">
                                        <img alt="profile" src={replyingTo?.created_by?.avatar} className="h-7 w-7 rounded-full object-cover bg-gray-300 dark:bg-[#333333]" />
                                    </div>
                                    <div className="flex flex-col w-full">
                                        <div className="flex justify-between items-center w-full">
                                            <p className="text-[11px] font-bold text-black dark:text-white">{replyingTo?.created_by?.full_name}</p>
                                            <IoCloseCircle onClick={() => { setReplyingTo(null); setEditingReply(null); setReplyBody(""); }} className="text-gray-500 cursor-pointer text-[18px] hover:text-gray-700" />
                                        </div>
                                        <p className="text-[10px] mt-[3px] break-words text-black dark:text-white">{replyingTo?.body}</p>
                                    </div>
                                </div>
                            </div>
                            <div className="w-full z-50 flex">
                                <div className="h-8 w-11">
                                    <img alt="profile" src={User_profile?.avatar} className="h-8 w-8 rounded-full object-cover bg-gray-300 dark:bg-[#333333]" />
                                </div>
                                <div className="flex justify-start w-full relative">
                                    <input
                                        type="text"
                                        ref={inputRef}
                                        value={replyBody}
                                        onChange={(e) => setReplyBody(e.target.value)}
                                        onKeyDown={handleReplyKeyDown}
                                        placeholder="Type your reply"
                                        className="text-[14px] bg-gray-200 dark:bg-[#333333] w-full text-black dark:text-white pl-2 pr-7 py-1 outline-none rounded"
                                    />
                                    <BsArrowUpCircle onClick={handleSubmitReply} className="absolute right-1 w-5 top-[3px] text-blue-600 hover:text-blue-700 text-[22px] cursor-pointer" />
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </Drawer>

            {/* Report Comment Modal */}
            {reportingCommentId && (
                <div className="fixed inset-0 bg-black bg-opacity-80 z-[60] flex items-center justify-center">
                    <div className="bg-white dark:bg-[#1E1D21] rounded-lg w-[300px] p-4">
                        <p className="text-[18px] font-bold text-center text-black dark:text-white mb-2">Report Comment</p>
                        <label className="block text-[12px] mb-0.5 font-semibold text-black dark:text-white">
                            Reason <span className="text-red-500">*</span>
                        </label>
                        <div className="relative mb-2">
                            <div
                                onClick={() => setShowReportReasonDropdown(!showReportReasonDropdown)}
                                className="w-full h-[35px] border border-gray-400 dark:border-gray-800/50 bg-white dark:bg-[#1E1D21] rounded-lg px-2 text-[12px] flex items-center justify-between cursor-pointer"
                            >
                                <span className={reportReason ? "text-black dark:text-white" : "text-gray-500"}>
                                    {reportReason || "Select a reason"}
                                </span>
                                <IoIosArrowDown className="text-gray-500" />
                            </div>
                            {showReportReasonDropdown && (
                                <div className="absolute top-[36px] left-0 w-full bg-white dark:bg-black border border-gray-400 rounded-lg shadow-lg z-10 max-h-[125px] overflow-y-auto">
                                    {REPORT_REASONS.map((r, i) => (
                                        <div
                                            key={i}
                                            onClick={() => { setReportReason(r); setShowReportReasonDropdown(false); }}
                                            className="px-2 py-1.5 text-[12px] text-black dark:text-white hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer border-b border-gray-200 dark:border-gray-700 last:border-b-0"
                                        >
                                            {r}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <label className="block text-[12px] mb-0.5 font-semibold text-black dark:text-white">Optional Details</label>
                        <textarea
                            value={reportMessage}
                            onChange={(e) => setReportMessage(e.target.value)}
                            maxLength={200}
                            placeholder="Additional details..."
                            className="w-full h-[50px] border border-gray-400 dark:border-gray-800/50 dark:bg-[#1E1D21] rounded-lg p-1.5 text-[12px] focus:outline-none resize-none"
                        />
                        <div className="flex gap-2 mt-3 justify-center">
                            <Button
                                onClick={() => { setReportingCommentId(null); setReportReason(""); setReportMessage(""); setShowReportReasonDropdown(false); }}
                                size="sm"
                                className="bg-[#1b1b1b] dark:bg-gray-400 dark:text-black text-white"
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleSubmitReport}
                                size="sm"
                                className="bg-red-900 hover:bg-red-600 text-white"
                            >
                                Report
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Block Confirm Modal */}
            {showBlockConfirm && blockUser && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center">
                    <div className="bg-white dark:bg-[#1E1D21] rounded-lg w-[300px] py-4 flex flex-col items-center">
                        <p className="text-[20px] font-bold text-black dark:text-white text-center mb-2">
                            Block {blockUser.full_name}?
                        </p>
                        <p className="text-[12px] text-black dark:text-white text-center px-4">
                            They will not be able to send you messages, find your profile, or see your posts, and they will not be notified that you blocked them.
                        </p>
                        <div className="flex gap-2 mt-4">
                            <Button
                                onClick={() => { setShowBlockConfirm(false); setBlockUser(null); }}
                                size="sm"
                                className="bg-[#1b1b1b] dark:bg-gray-400 dark:text-black text-white"
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleBlockConfirm}
                                size="sm"
                                className="bg-red-900 hover:bg-red-600 text-white"
                            >
                                Block
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
